import { useEffect, useRef } from "react";
import { Store } from "react-notifications-component";

// Detecta deploys novos enquanto a aba do SPA fica aberta.
// Lê /version.json (servido pelo nginx do front, sem cache) no boot e a cada
// CHECK_INTERVAL_MS; se o buildId mudar em relação ao do carregamento, mostra
// um toast não-bloqueante "Atualizar" (não recarrega sozinho — preserva
// formulários em preenchimento).
const CHECK_INTERVAL_MS = 10 * 60 * 1000; // 10 minutos
const UPDATE_TOAST_ID = "app-update-available";

async function fetchBuildId() {
  const res = await fetch(`/version.json?t=${Date.now()}`, {
    cache: "no-store",
  });
  if (!res.ok) throw new Error(`version check: ${res.status}`);
  return (await res.json())?.buildId;
}

async function clearCachesAndReload() {
  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }
  } catch {
    // segue para o reload mesmo se a limpeza de cache falhar
  }
  window.location.reload();
}

function showUpdateToast() {
  Store.addNotification({
    id: UPDATE_TOAST_ID, // evita empilhar toasts duplicados
    container: "top-left",
    insert: "right",
    animationIn: ["animate__animated", "animate__fadeInRight"],
    dismiss: { duration: 0, click: false, showIcon: true }, // persistente
    content: (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          padding: "14px 16px",
          borderRadius: 8,
          background: "#1f2430",
          color: "#fff",
          boxShadow: "0 6px 20px rgba(0,0,0,0.25)",
        }}
      >
        <strong>Nova versão disponível</strong>
        <span style={{ fontSize: 13, opacity: 0.85 }}>
          Atualize para carregar a versão mais recente do sistema.
        </span>
        <button
          type="button"
          onClick={clearCachesAndReload}
          style={{
            alignSelf: "flex-start",
            marginTop: 4,
            padding: "6px 14px",
            border: "none",
            borderRadius: 6,
            cursor: "pointer",
            fontWeight: 600,
            background: "#fff",
            color: "#1f2430",
          }}
        >
          Atualizar agora
        </button>
      </div>
    ),
  });
}

export default function useVersionCheck() {
  const baseline = useRef(null);
  const notified = useRef(false);

  useEffect(() => {
    let stopped = false;

    const check = async () => {
      if (notified.current) return;
      try {
        const current = await fetchBuildId();
        if (stopped || !current) return;

        if (baseline.current == null) {
          baseline.current = current; // versão com que a página carregou
          return;
        }

        if (current !== baseline.current) {
          notified.current = true;
          showUpdateToast();
        }
      } catch {
        // deploy em andamento / rede indisponível: tenta no próximo ciclo
      }
    };

    check(); // fixa o baseline no boot
    const intervalId = setInterval(check, CHECK_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      stopped = true;
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);
}
